import { Vote } from './vote';
import { Media } from './media';
import { animals } from './animals';
import { Coords } from './coords';
import { IpInfo } from './ip-info';
import { ChallengeStateMap } from './challenge-state';
import { DatabaseModel } from './database-model';

export interface AuthCredential {
    providerId: string;
    signInMethod: string;
}

//import { DeviceInfo } from 'ngx-device-detector/device-detector.service';
export interface DeviceInfo {
    userAgent: string;
    os: string;
    browser: string;
    device: string;
    os_version: string;
    browser_version: string;
}

export interface MoteUnlockState {
    moteKey: string,
    tourKey: string,
    userKey: string,
    projectKey: string,
    createdOn: any
}

export interface DeviceTokenInfo {
    token: string;
    deviceKey: string;
    lastUpdatedOn: Date;
}

export interface EmailListSubscriptionState {
    isSubscribed: boolean;
    updatedOn: Date | any;
}

export class User {
    public static INIT_USER_RADIUS = 20;

    // Public user information
    public key: string = null;
    public userName: string = "";
    public profilePic: Media = new Media();
    public coverImage: Media = new Media();
    public about: string = "";
    public country: string = "";
    public city: string = "";
    public gender: string = "";
    public organization: string = "";
    public countryCode: string = "";
    public externalLink: string = "";
    public noName: boolean = false;
    public userRadius: number = User.INIT_USER_RADIUS;
    public votes: { [key: string]: Vote };
    public predefinedProfilePic: string = '';
    public isEmailPermitted: boolean = false;
    public emailPermittedUpdatedOn: Date = null;
    public emailListSubscriptions: { [subscriptionKey: string]: EmailListSubscriptionState } = {};

    // Assorted
    public unlockedMotes: MoteUnlockState[] = [];
    public logins: Array<any> = [];
    public isAnonymous: boolean = true;
    public isAnonymousAccount: boolean = false;
    public challengeStateMap: ChallengeStateMap = new ChallengeStateMap({});

    // Group
    public groupKey: string = '';

    // Private user information
    public email: string = "";

    public createdOn: any = "";
    public updatedOn: number | any;
    public adminRequestOn: number = 0;
    public createdOnHtml = "";
    // @deprecated Use project service to access active project
    // TODO: Delete dependency from Studio
    public activeProject: string = "";
    // @deprecated Use project service to access active route
    public activeRoute: string = "";

    // Private static array of cached user objects
    private static userCache: { [key: string]: User } = {};
    private authenticated: boolean = false;

    // Provider data, login data, admin option
    public providerData: any = null;
    public isAdmin: any = false;
    public isSuperAdmin: boolean = false;
    public xp: number = -1;

    // Users device info
    public deviceInfo: DeviceInfo = null;
    public ipInfo: IpInfo = null;
    public deviceTokenInfo: { [deviceKey: string]: DeviceTokenInfo } = {};

    // Last known user position, dynamically populated
    public position: Coords = new Coords(0, 0, false);

    constructor(key: string) {
        this.key = key;
    }

    static fromPublicJson(key: string, json: any): User {
        // Wrap DB object in public container and create user object
        return User.fromJson(key, { public: json });
    }

    static fromPrivateJson(key: string, json: any): User {
        // Wrap DB object in public container and create user object
        return User.fromJson(key, { private: json });
    }

    static fromJson(key, json): User {
        // Get from cache if it exists
        let user = User.userCache[key];

        if(!user) {
            // Create new mote
            user = new User(key);
            // Save mote object in cache
            User.userCache[key] = user;
        }
        // Update user instance with new content
        user.updateFromJson(json);
        return user;
    }

    public toPublicJson(): any {
        let profilePic = null;
        let coverImage = null;
        if(this.profilePic) profilePic = this.profilePic.toJson();
        if(this.coverImage) coverImage = this.coverImage.toJson();

        return {
            updatedOn: this.updatedOn,
            userName: this.userName,
            profilePic: profilePic,
            coverImage: coverImage,
            about: this.about,
            country: this.country,
            countryCode: this.countryCode,
            organization: this.organization,
            externalLink: this.externalLink,
            isAnonymousAccount: this.isAnonymousAccount,
            city: this.city,
            gender: this.gender,
            votes: this.modelMapToJson(this.votes),
            challengeStateMap: this.challengeStateMap ? this.challengeStateMap.toJson() : null,
            predefinedProfilePic: this.predefinedProfilePic,
            isEmailPermitted: this.isEmailPermitted,
            emailPermittedUpdatedOn: this.emailPermittedUpdatedOn,
            emailListSubscriptions: this.emailListSubscriptions
        };
    }

    public toPrivateJson(): any {
        return {
            email: this.email,
            createdOn: this.createdOn,
            updatedOn: this.updatedOn,
            adminRequestOn: this.adminRequestOn,
            activeProject: this.activeProject,
            activeRoute: this.activeRoute,
            providerData: this.providerData,
            isAdmin: this.isAdmin,
            isSuperAdmin: this.isSuperAdmin,
            deviceInfo: this.deviceInfo,
            ipInfo: this.ipInfo,
            city: this.city,
            gender: this.gender,
            groupKey: this.groupKey,
            unlockedMotes: DatabaseModel.arrayToMap(this.unlockedMotes as unknown as DatabaseModel[], 'moteKey'),
            deviceTokenInfo: this.deviceTokenInfo,
        };
    }

    public toJson(): any {
        return {
            public: this.toPublicJson(),
            private: this.toPrivateJson()
        };
    }

    public getUnlockedKeys(): string[] {
        // Return list of motes keys unlocked by user
        return this.unlockedMotes.map(unlocked => unlocked.moteKey);
    }

    // Get loved motes votes
    public getLovedVotes(): Array<Vote> {
        if(!this.votes) return [];
        // Convert votes map to array
        return Object.keys(this.votes).map(key => this.votes[key]);
    }

    public getLovedKeys(): string[] {
        if(!this.votes) return [];
        // Return list of mote keys voted for by user
        return Object.keys(this.votes).map(key => this.votes[key].moteKey);
    }

    public updateFromJson(json: any): User {
        this.noName = false;
        if(json.public) {
            // Public user information
            // Obs: updated on will be overwritten by the private updatedOn in most cases (it is shared between private/public in model but not in database)
            this.updatedOn = json.public.updatedOn || 0;
            this.userName = json.public.userName || "Anonymous " + this.assignRandomName();
            this.profilePic = new Media(json.public.profilePic);
            this.coverImage = new Media(json.public.coverImage);
            this.about = json.public.about || "";
            this.country = json.public.country || "";
            this.organization = json.public.organization || "";
            this.countryCode = json.public.countryCode || "";
            this.externalLink = json.public.externalLink || "";
            this.isAnonymousAccount = json.public.isAnonymousAccount || false;
            this.votes = this.jsonToModelMap<Vote>(Vote, json.public.votes) || {};
            this.challengeStateMap = new ChallengeStateMap(json.public.challengeStateMap ? json.public.challengeStateMap : {});
            this.predefinedProfilePic = json.public.predefinedProfilePic || "";
            this.isEmailPermitted = json.public.isEmailPermitted || false;
            this.emailPermittedUpdatedOn = json.public.emailPermittedUpdatedOn ? new Date(json.public.emailPermittedUpdatedOn) : null;
            this.emailListSubscriptions = json.public.emailListSubscriptions || {};
        }

        if(json.private) {
            // Private User information
            this.email = json.private.email
                || json.private?.providerData?.email // For some old fb-users, the email is hidden in providerData
                || "";
            this.createdOn = json.private.createdOn || "";
            this.updatedOn = json.private.updatedOn || 0;
            this.adminRequestOn = json.private.adminRequestOn || 0;
            this.activeProject = json.private.activeProject || "";
            this.activeRoute = json.private.activeRoute || "";

            this.createdOnHtml = this.createdOn ? (new Date(this.createdOn)).toJSON().slice(0, 10).replace(/-/g, '/') : "";
            this.providerData = json.private.providerData || "";
            this.isAdmin = json.private.isAdmin || false;
            this.isSuperAdmin = json.private.isSuperAdmin || false;
            //this.numberOfLogins = Object.keys(json.private.logins || []).length;
            this.deviceInfo = json.private.deviceInfo || {};
            this.ipInfo = json.private.ipInfo || {};

            this.gender = json.private.gender || '';
            this.city = json.private.city || '';
            this.groupKey = json.private.groupKey || '';

            this.deviceTokenInfo = json.private.deviceTokenInfo || {};

            // Update Unlocked array
            this.unlockedMotes = DatabaseModel.mapToArray(json.private.unlockedMotes);

            // Update login array
            this.logins = [];
            for(let key in json.private.logins) {
                let login = json.private.logins[key];
                this.logins.push(login);
            }
        }
        return this;
    }

    public isAuthenticated(): boolean {
        return this.authenticated;
    }

    public getProviderData(): any {
        return this.providerData;
    }

    // public setAuthenticated(isAuthenticated:boolean, providerData:any=null, loginData:any=null, anonymous:boolean = false):void {
    // 	this.authenticated = isAuthenticated;
    // 	this.providerData = providerData;
    //     this.loginData = loginData;
    //     this.isAnonymous = anonymous;
    // }

    public assignRandomName(): string {
        this.noName = true;
        return animals[Math.floor(Math.random() * animals.length)];
    }

    public updateXp(): void {
        this.xp = (this.unlockedMotes.length);
    }

    /**
     * Convert a javascript map with model instances
     * to a map with pure json (for database to digest)
     * Assumes that T has a toJson function
     */
    private modelMapToJson(modelMap: { [key: string]: any }): { [key: string]: any } {
        if(!modelMap) return {};

        return Object.keys(modelMap).reduce((acc, key) => {
            let model = modelMap[key];
            acc[key] = model.toJson();
            return acc;
        }, {});
    }

    /**
     * Convert a javascript map to a map with instances
     * of the given model type T aka. Mote, Vote, User model types
     * Assumes that model take json in the constructor
     */
    private jsonToModelMap<T>(Model, json): { [key: string]: T } {
        let modelKeys = Object.keys(json || {});
        // Convert json into a object with model instances if the given type
        return modelKeys.reduce((acc, key) => {
            // Initiate model class from json
            acc[key] = new Model(json[key]);
            return acc;
        }, {});
    }

    public static getCachedUser(key: string) {
        return User.userCache[key];
    }

    /**
     * Get number of unlocked stories in a tour
     */
    public getTourUnlockCount(tourKey: string): number {
        return this.unlockedMotes.filter(unlock => unlock.tourKey == tourKey).length;
    }

    /**
     * Get number of unlocked stories in a project
     */
    public getProjectUnlockCount(projectKey: string): number {
        return this.unlockedMotes.filter(unlock => unlock.projectKey == projectKey).length;
    }

}
